import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../redux/store";
import { loginUser } from "../redux/slices/authSlice";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  CircularProgress,
  Typography,
  Container,
  Box,
  Grid,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogActions,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import useTabTitle from "../hooks/useTabTitle.hook";
import {
  inputStyle,
  primaryBtnStyle,
  primaryBtnStyleOutline,
} from "../styles/styling";
import { PermissionsByDocKey } from "../interfaces/permissions-interface";
import { transformArrToObj } from "../utils/transform-permissions-obj";

const malayanBanner = require("../assets/logo/malayan-logo.png");

const LoginPage: React.FC = () => {
  useTabTitle();

  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string>("");
  const [passwordError, setPasswordError] = useState<string>("");
  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);
  const [showError, setShowError] = useState<boolean>(false);

  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { status, error } = useSelector((state: RootState) => state.auth);

  const handleToggle = () => {
    setShowPassword((prev) => !prev);
  };

  const handleLogin = async () => {
    setEmailError("");
    setPasswordError("");

    let hasError = false;

    if (email.trim() === "") {
      setEmailError("Email is required");
      hasError = true;
    }
    if (password.trim() === "") {
      setPasswordError("Password is required");
      hasError = true;
    }

    if (hasError) {
      return;
    }

    await dispatch(loginUser({ email, password }))
      .unwrap()
      .then((result: { token: string; currentUser: any }) => {
        const permissionsByKey: PermissionsByDocKey = transformArrToObj(
          result?.currentUser?.userPermissions ?? []
        );

        if (permissionsByKey?.user?.permissions?.view) {
          navigate("/dashboard");
        } else if (permissionsByKey?.userRole?.permissions?.view) {
          navigate("/manage-user-role");
        } else if (permissionsByKey?.productsList?.permissions?.view) {
          navigate("/products");
        } else if (permissionsByKey?.notification?.permissions?.view) {
          navigate("/notification");
        } else if (permissionsByKey?.contactSupport?.permissions?.view) {
          navigate("/support");
        } else if (permissionsByKey?.helpCenter?.permissions?.view) {
          navigate("/help-center");
        } else if (permissionsByKey?.termsAndConditions?.permissions?.view) {
          navigate("/terms-and-conditions");
        } else if (permissionsByKey?.auditTrail?.permissions?.view) {
          navigate("/audit-trail");
        } else if (permissionsByKey?.mobileAppIdleTime?.permissions?.view) {
          navigate("/idle-time");
        } else {
          navigate("/dashboard");
        }
      })
      .catch(() => {
        setShowError(true);
      });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleLogin();
  };

  useEffect(() => {
    if (emailError) {
      setEmailError("");
    }
    if (passwordError) {
      setPasswordError("");
    }
    if (error) {
      setShowError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, password]);

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          alignSelf: "center",
          marginTop: "120px",
        }}
      >
        <img
          srcSet={malayanBanner}
          src={malayanBanner}
          alt="Malayan"
          loading="lazy"
          style={{
            objectFit: "contain",
            width: "600px",
            height: "165px",
            gap: "10px",
            marginBottom: 40,
          }}
        />
        <Box component="form" width="90%" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!emailError}
                helperText={emailError}
                sx={inputStyle()}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Password"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!passwordError}
                helperText={passwordError}
                sx={inputStyle()}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={handleToggle} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={status === "loading" || !email || !password}
                sx={primaryBtnStyle}
              >
                {status === "loading" ? (
                  <CircularProgress size={24} />
                ) : (
                  "Login"
                )}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                fullWidth
                disabled={status === "loading"}
                onClick={() => setShowForgotPassword(true)}
                sx={primaryBtnStyleOutline}
              >
                Forgot Password
              </Button>
            </Grid>
            {error && showError && (
              <Grid item xs={12}>
                <Typography color="error" align="center">
                  {error}
                </Typography>
              </Grid>
            )}

            <Dialog
              open={showForgotPassword}
              onClose={() => setShowForgotPassword(false)}
              aria-labelledby="alert-dialog-title"
              aria-describedby="alert-dialog-description"
            >
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  Contact your system admin to reset your password
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowForgotPassword(false)} autoFocus>
                  Got it!
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
        </Box>
      </Box>
    </Container>
  );
};

export default LoginPage;
